import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Send, Paperclip, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../utils/api';
import PhoneInput from '../common/PhoneInput';

const MAX_BYTES = 30 * 1024 * 1024;
const MAX_FILES = 20;

const SuggestionForm = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [nume, setNume] = useState(user?.nume || '');
  const [firma, setFirma] = useState(user?.firma || '');
  const [telefon, setTelefon] = useState(user?.telefon || '');
  const isGuestEmail = (user?.email || '').endsWith('@temp.local');
  const [email, setEmail] = useState(isGuestEmail ? '' : (user?.email || ''));
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const onFileSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    const ok = [];
    let rejected = 0;
    for (const f of picked) {
      if (f.size > MAX_BYTES) { rejected++; continue; }
      ok.push(f);
    }
    const merged = [...files, ...ok];
    if (merged.length > MAX_FILES) {
      toast.error(t('suggestion.error_too_many', { max: MAX_FILES, defaultValue: 'Maxim {{max}} fisiere.' }));
      setFiles(merged.slice(0, MAX_FILES));
    } else {
      setFiles(merged);
    }
    if (rejected) {
      toast.error(t('suggestion.error_too_large', 'Atasament prea mare. Maxim 30 MB per fisier.'));
    }
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) {
      toast.error(t('suggestion.error_empty', 'Va rugam scrieti un mesaj.'));
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('nume', nume);
      fd.append('firma', firma);
      fd.append('telefon', telefon);
      fd.append('email', email);
      fd.append('text', text);
      files.forEach((f) => fd.append('attachments', f));

      const res = await fetch(`${API_URL}/api/contact/send-suggestion`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Eroare');
      toast.success(t('suggestion.success', 'Multumim, mesajul a fost trimis!'));
      setText('');
      setFiles([]);
    } catch (err) {
      toast.error(t('suggestion.error_send', 'Eroare la trimitere: ') + (err.message || ''));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="suggestion-form">
      <h3 className="text-base font-semibold text-slate-900 mb-2">{t('suggestion.title', 'Sugestii si feedback')}</h3>
      <p className="text-sm text-slate-600 mb-3">{t('suggestion.intro', 'Daca aveti sugestii sau doriti sa ne ajutati sa imbunatatim aplicatia, va rugam scrieti mai jos.')}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-600">{t('suggestion.nume', 'Nume')}</label>
          <input value={nume} onChange={(e) => setNume(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="suggestion-nume" />
        </div>
        <div>
          <label className="text-xs text-slate-600">{t('suggestion.firma', 'Firma')}</label>
          <input value={firma} onChange={(e) => setFirma(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="suggestion-firma" />
        </div>
        <div>
          <label className="text-xs text-slate-600">{t('suggestion.telefon', 'Telefon')}</label>
          <PhoneInput value={telefon} onChange={setTelefon} testid="suggestion-telefon" />
        </div>
        <div>
          <label className="text-xs text-slate-600">{t('suggestion.email', 'Email')}</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemplu.ro" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="suggestion-email" />
        </div>
      </div>

      <div>
        <label className="text-xs text-slate-600">{t('suggestion.text', 'Mesajul dumneavoastra')}</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
          placeholder={t('suggestion.text_placeholder', 'Scrieti aici sugestiile sau intrebarile...')}
          data-testid="suggestion-text"
          required
        />
      </div>

      {files.length > 0 && (
        <ul className="space-y-1" data-testid="suggestion-files-list">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1">
              <span className="truncate flex-1 mr-2">{f.name} <span className="text-slate-400">({Math.round(f.size / 1024)} KB)</span></span>
              <button type="button" onClick={() => removeFile(i)} className="text-slate-500 hover:text-red-600" data-testid={`suggestion-file-remove-${i}`} aria-label="remove">
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-md cursor-pointer text-sm text-slate-700" data-testid="suggestion-attachment-label">
          <Paperclip className="h-4 w-4" />
          {t('suggestion.attach_multi', 'Atasamente (max 20 fisiere x 30 MB)')}
          <input type="file" multiple onChange={onFileSelect} className="hidden" />
        </label>
        <button
          type="submit"
          disabled={sending}
          className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-md text-sm font-semibold disabled:opacity-50 transition-colors"
          style={{ backgroundColor: '#2E7D5B' }}
          onMouseEnter={(e) => { if (!sending) e.currentTarget.style.backgroundColor = '#25664A'; }}
          onMouseLeave={(e) => { if (!sending) e.currentTarget.style.backgroundColor = '#2E7D5B'; }}
          data-testid="suggestion-submit"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {t('suggestion.submit', 'Trimite mesaj')}
        </button>
      </div>
    </form>
  );
};

export default SuggestionForm;
